import { Badge, Button, Checkbox, DialogContent, DialogTitle } from '@atlas/ui-primitives'
import {
	ARTICLE_STATUS_LABELS,
	ARTICLE_TRANSLATABLE_CONTENT_LIMIT,
	ARTICLE_TRANSLATE_LOCALES_LIMIT,
	ArticleStatus,
	type ArticleTranslationVariant,
} from '@/domain/article.types'
import { type LocaleLabels, localeLabel } from '@/lib/locale-identity'
import { ArticleTranslationBadge } from './ArticleTranslationBadge'

/**
 * Only a draft may be re-translated: machine-overwriting a published or
 * in-review variant would be an unreviewed content change to live editorial
 * content, and translating an archived one is almost certainly a mistake. The
 * endpoint refuses all three; this is the same rule, stated where the user is
 * choosing.
 */
const isRetranslatable = (variant: ArticleTranslationVariant) => variant.status === ArticleStatus.DRAFT

/** A locale the group does not have yet. */
function NewLocaleOption({
	option,
	isSelected,
	onToggle,
}: {
	option: { code: string; label: string }
	isSelected: boolean
	onToggle: (code: string) => void
}) {
	return (
		<Checkbox size="sm" isSelected={isSelected} onChange={() => onToggle(option.code)}>
			<span className="text-ds-sm text-ds-text">{option.label}</span>
		</Checkbox>
	)
}

/** A locale already in the group: selectable only while its article is a draft. */
function ExistingVariantRow({
	variant,
	labels,
	isSelected,
	onToggle,
}: {
	variant: ArticleTranslationVariant
	labels: LocaleLabels
	isSelected: boolean
	onToggle: (code: string) => void
}) {
	const label = localeLabel(variant.languageIso, labels)

	return (
		<li className="flex flex-wrap items-center gap-2 rounded-ds-md border border-ds-border px-3 py-2">
			<span className="min-w-16 text-ds-sm font-medium text-ds-text">{label}</span>
			<Badge variant="secondary" size="sm">
				{ARTICLE_STATUS_LABELS[variant.status]}
			</Badge>
			<ArticleTranslationBadge status={variant.translationStatus} />
			{variant.translationStatus && variant.translationFailureReason ? (
				<span className="w-full whitespace-normal break-words text-ds-sm text-ds-danger">
					{variant.translationFailureReason}
				</span>
			) : null}
			<span className="ml-auto">
				{isRetranslatable(variant) ? (
					<Checkbox size="sm" isSelected={isSelected} onChange={() => onToggle(variant.languageIso)}>
						<span className="text-ds-sm text-ds-text-muted">Replace</span>
					</Checkbox>
				) : (
					<span className="text-ds-sm text-ds-text-muted">
						Move it to {ARTICLE_STATUS_LABELS[ArticleStatus.DRAFT]} to re-translate
					</span>
				)}
			</span>
		</li>
	)
}

/**
 * The two picker sections: the locales the group lacks, then the ones it has.
 *
 * Split out so the dialog shell stays inside the function-length limit, not
 * because either half is reused.
 */
function PickerSections({
	offerableLocales,
	existingVariants,
	labels,
	selected,
	onToggle,
}: Pick<ArticleTranslateDialogProps, 'offerableLocales' | 'existingVariants' | 'labels' | 'selected' | 'onToggle'>) {
	return (
		<>
			<h3 className="mt-5 font-mono text-ds-2xs uppercase tracking-ds-widest text-ds-text-subtle">Translate into</h3>
			{offerableLocales.length === 0 ? (
				<p className="mt-2 text-ds-sm text-ds-text-muted">
					No further locales are available — every locale the registry offers is already in this group.
				</p>
			) : (
				<div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
					{offerableLocales.map((option) => (
						<NewLocaleOption
							key={option.code}
							option={option}
							isSelected={selected.has(option.code)}
							onToggle={onToggle}
						/>
					))}
				</div>
			)}

			{existingVariants.length > 0 ? (
				<>
					<h3 className="mt-6 font-mono text-ds-2xs uppercase tracking-ds-widest text-ds-text-subtle">
						Already in this group
					</h3>
					<ul className="mt-2 flex flex-col gap-2">
						{existingVariants.map((variant) => (
							<ExistingVariantRow
								key={variant.articleId}
								variant={variant}
								labels={labels}
								isSelected={selected.has(variant.languageIso)}
								onToggle={onToggle}
							/>
						))}
					</ul>
				</>
			) : null}
		</>
	)
}

/** What the confirm button is about to do, stated before it is pressed. */
function RequestSummary({ newCount, replaceCount }: { newCount: number; replaceCount: number }) {
	const parts = [
		newCount > 0 ? `${newCount} new locale${newCount === 1 ? '' : 's'}` : null,
		replaceCount > 0 ? `${replaceCount} will be replaced` : null,
	].filter(Boolean)

	if (parts.length === 0) return <p className="text-ds-sm text-ds-text-muted">Select at least one locale.</p>
	return <p className="text-ds-sm text-ds-text-muted">{parts.join(' · ')}</p>
}

export interface ArticleTranslateDialogProps {
	isOpen: boolean
	/** Locales the group does not have, from the `locale` registry axis. Empty while the group is still loading. */
	offerableLocales: readonly { code: string; label: string }[]
	/** The group's non-source variants, so an existing locale can be re-requested or explained. */
	existingVariants: readonly ArticleTranslationVariant[]
	labels: LocaleLabels
	/** Locale codes currently selected, new and to-be-replaced alike. */
	selected: ReadonlySet<string>
	onToggle: (code: string) => void
	/** The source body's length, so an over-cap article is refused here rather than by the API. */
	sourceContentLength: number
	onConfirm: () => void
	onCancel: () => void
	isPending?: boolean
	error?: string | null
}

/**
 * The request surface: pick locales, see what would be created versus replaced,
 * confirm once.
 *
 * One request for every selected locale, which is what the endpoint is shaped
 * for. An overwrite is named in the footer rather than behind a `window.confirm`
 * — a browser confirm cannot say "two of your five picks will replace
 * hand-edited text", and stacking one on an open dialog is worse than stating
 * the consequence where the user is already looking.
 */
export function ArticleTranslateDialog({
	isOpen,
	offerableLocales,
	existingVariants,
	labels,
	selected,
	onToggle,
	sourceContentLength,
	onConfirm,
	onCancel,
	isPending = false,
	error,
}: ArticleTranslateDialogProps) {
	if (!isOpen) return null

	const isOverCap = sourceContentLength > ARTICLE_TRANSLATABLE_CONTENT_LIMIT
	const replaceCount = existingVariants.filter((v) => selected.has(v.languageIso)).length
	const newCount = selected.size - replaceCount
	const isOverLocaleLimit = selected.size > ARTICLE_TRANSLATE_LOCALES_LIMIT

	return (
		<DialogContent size="xl" isOpen={isOpen} isKeyboardDismissDisabled={isPending} className="overflow-y-auto">
			<DialogTitle>Translate this article</DialogTitle>

			{isOverCap ? (
				<p role="alert" className="mt-3 text-ds-sm text-ds-danger">
					This article is {sourceContentLength.toLocaleString()} characters; translation is capped at{' '}
					{ARTICLE_TRANSLATABLE_CONTENT_LIMIT.toLocaleString()}. Shorten the body to translate it.
				</p>
			) : (
				<PickerSections
					offerableLocales={offerableLocales}
					existingVariants={existingVariants}
					labels={labels}
					selected={selected}
					onToggle={onToggle}
				/>
			)}

			{isOverLocaleLimit ? (
				<p role="alert" className="mt-4 text-ds-sm text-ds-danger">
					Select at most {ARTICLE_TRANSLATE_LOCALES_LIMIT} locales for one request.
				</p>
			) : null}
			{error ? (
				<p role="alert" className="mt-4 whitespace-normal break-words text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}

			<div className="mt-6 flex flex-wrap items-center justify-between gap-3">
				{isOverCap ? <span /> : <RequestSummary newCount={newCount} replaceCount={replaceCount} />}
				<div className="ml-auto flex items-center gap-2">
					<Button type="button" variant="secondary" className="rounded-ds-full" onPress={onCancel}>
						Cancel
					</Button>
					<Button
						type="button"
						className="rounded-ds-full px-5"
						isDisabled={isOverCap || isPending || selected.size === 0 || isOverLocaleLimit}
						onPress={onConfirm}
					>
						{isPending ? 'Requesting…' : `Translate (${selected.size})`}
					</Button>
				</div>
			</div>
		</DialogContent>
	)
}
